import { useState } from "react";
import { CalendarDays, RotateCcw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

// Values are "YYYY-MM-DD" strings (what the API expects), shown as dd-mm-yyyy.
// Dates are built from local calendar parts, never toISOString(), so the
// picked day cannot shift by a timezone offset.
const toKey = (date) =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");

const fromKey = (key) => {
  const [year, month, day] = key.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const display = (key) => key.split("-").reverse().join("-");

function DatePicker({ value, placeholder, label, testid, disabled, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = value ? fromKey(value) : undefined;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          data-testid={testid}
          aria-label={label}
          title={label}
          className={`w-[150px] justify-start gap-2 bg-secondary/60 px-3 text-left font-normal ${
            value ? "" : "text-muted-foreground"
          }`}
        >
          <CalendarDays className="h-4 w-4 shrink-0 opacity-70" />
          {value ? display(value) : placeholder}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected}
          toDate={new Date()}
          disabled={disabled}
          onSelect={(date) => {
            // Clicking the selected day again clears it.
            onChange(date ? toKey(date) : "");
            setOpen(false);
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

export default function DateRangeFilter({ from, to, onChange }) {
  const today = new Date();

  return (
    <div className="flex items-center gap-2">
      <DatePicker
        value={from}
        placeholder="From date"
        label="Created from date"
        testid="tickets-from-date"
        disabled={{ after: today }}
        onChange={(value) =>
          // Keep the range valid: a later start pulls the end along.
          onChange(value, value && to && value > to ? value : to)
        }
      />

      <span className="text-xs text-muted-foreground">to</span>

      <DatePicker
        value={to}
        placeholder="To date"
        label="Created to date"
        testid="tickets-to-date"
        disabled={[
          { after: today },
          ...(from ? [{ before: fromKey(from) }] : []),
        ]}
        onChange={(value) => onChange(from, value)}
      />

      {(from || to) && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          title="Clear date filter"
          aria-label="Clear date filter"
          data-testid="tickets-clear-date"
          onClick={() => onChange("", "")}
        >
          <RotateCcw className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
